import { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { FaTimes, FaCheck, FaMapMarkerAlt } from "react-icons/fa";
import "leaflet/dist/leaflet.css";

const pinIcon = L.divIcon({
  className: "modern-map-pin",
  html: `
    <div class="modern-map-pin__wrap">
      <span class="modern-map-pin__pulse" style="background:#f59e0b"></span>
      <span class="modern-map-pin__dot" style="background:#f59e0b">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="#0b1f44" aria-hidden="true">
          <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z"/>
        </svg>
      </span>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 36],
});

const ClickHandler = ({ onSelect }) => {
  useMapEvents({
    click(e) {
      onSelect({
        lat: e.latlng.lat,
        lng: e.latlng.lng,
      });
    },
  });
  return null;
};

const RecenterMap = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 80);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom(), { animate: true });
    }
  }, [center, map]);

  return null;
};

const MapLocationPicker = ({
  open,
  onClose,
  onConfirm,
  initialLat,
  initialLng,
}) => {
  const fallbackCenter = useMemo(() => [27.7172, 85.324], []);
  const hasInitial =
    initialLat !== "" &&
    initialLat != null &&
    initialLng !== "" &&
    initialLng != null &&
    !Number.isNaN(Number(initialLat)) &&
    !Number.isNaN(Number(initialLng));

  const [selected, setSelected] = useState(null);
  const [addressHint, setAddressHint] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (hasInitial) {
      setSelected({
        lat: Number(initialLat),
        lng: Number(initialLng),
      });
    } else {
      setSelected(null);
    }
    setAddressHint("");
  }, [open, hasInitial, initialLat, initialLng]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!selected) return undefined;

    let cancelled = false;
    const lookup = async () => {
      setLookingUp(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${selected.lat}&lon=${selected.lng}`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("lookup failed");
        const data = await res.json();
        if (!cancelled) {
          setAddressHint(data.display_name || "");
        }
      } catch {
        if (!cancelled) setAddressHint("");
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    };

    const timer = setTimeout(lookup, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selected]);

  if (!open) return null;

  const mapCenter = selected
    ? [selected.lat, selected.lng]
    : hasInitial
      ? [Number(initialLat), Number(initialLng)]
      : fallbackCenter;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-navy-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:h-[80vh] sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3 border-b border-navy-900/8 px-4 py-4 sm:px-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-600">
              Choose on map
            </p>
            <h3 className="mt-1 font-display text-xl font-semibold text-navy-900">
              Tap a point to set coordinates
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-surface p-2.5 text-navy-800 transition hover:bg-navy-900/10"
            aria-label="Close map picker"
          >
            <FaTimes />
          </button>
        </div>

        <div className="relative min-h-0 flex-1">
          <div className="modern-map h-full w-full">
            <MapContainer
              center={mapCenter}
              zoom={14}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={19}
              />
              <ZoomControl position="bottomright" />
              <ClickHandler onSelect={setSelected} />
              <RecenterMap center={mapCenter} />
              {selected && (
                <Marker position={[selected.lat, selected.lng]} icon={pinIcon} />
              )}
            </MapContainer>
          </div>

          <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-navy-950/75 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur">
            Click anywhere on the map
          </div>
        </div>

        <div className="border-t border-navy-900/8 bg-white px-4 py-4 sm:px-5">
          {selected ? (
            <div className="mb-3 rounded-2xl bg-surface px-3 py-2.5 text-sm text-navy-800">
              <div className="flex items-start gap-2">
                <FaMapMarkerAlt className="mt-0.5 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="font-medium">
                    {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
                  </p>
                  <p className="mt-0.5 truncate text-navy-800/65">
                    {lookingUp
                      ? "Looking up address..."
                      : addressHint || "Address lookup unavailable"}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="mb-3 text-sm text-navy-800/65">
              No point selected yet. Tap the map to drop a pin.
            </p>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-navy-900/15 px-4 py-2.5 text-sm font-semibold text-navy-900 transition hover:bg-surface"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                onConfirm({
                  latitude: Number(selected.lat.toFixed(6)),
                  longitude: Number(selected.lng.toFixed(6)),
                  addressHint: addressHint || "",
                });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-navy-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaCheck />
              Use this location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLocationPicker;
